import React, { useEffect, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post, del } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1150, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 } as React.CSSProperties,
};

const STATE_NAMES: Record<string, string> = {
  opening: t('首轮报价'), countering: t('还价中'), concession: t('让步阶段'), final_offer: t('最终报价'),
  closed_won: t('✅ 成交'), closed_lost: t('❌ 流失'),
};

// ═══ 询盘 ═══
function InquiriesView() {
  const [rows, setRows] = useState<any[]>([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => get('/api/v1/inquiries').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const parse = async () => {
    if (!email) return alert('请粘贴询盘邮件');
    setBusy(true);
    try {
      const r: any = await post('/api/v1/inquiries', { raw_email: email });
      alert(`解析完成：意图=${r.data.intent}，${(r.data.products || []).length} 个产品需求`);
      setEmail(''); load();
    } catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  const negotiate = async (id: string) => {
    setBusy(true);
    try { const r: any = await post('/api/v1/negotiations', { inquiry_id: id }); alert(`谈判已开启，首轮报价已生成（轮次 ${r.data.round}）`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('新询盘解析')}</h3>
        <textarea style={{ ...S.input, width: '100%', height: 140, marginBottom: 8 }} placeholder={t('粘贴买家询盘邮件原文...')} value={email} onChange={e => setEmail(e.target.value)} />
        <button style={S.btnPrimary} disabled={busy} onClick={parse}>{busy ? '解析中...' : t('🤖 解析询盘')}</button>
      </div>
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('买家')}</th><th style={S.th}>{t('意图')}</th><th style={S.th}>{t('需求摘要')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('操作')}</th></tr></thead>
          <tbody>
            {rows.map((r: any) => {
              const p = JSON.parse(r.parsed_json || '{}');
              return (
                <tr key={r.id}>
                  <td style={S.td}>{r.buyer_company || r.buyer_email || '-'}</td>
                  <td style={S.td}>{r.intent}</td>
                  <td style={{ ...S.td, fontSize: 12, maxWidth: 320 }}>{p.summary}</td>
                  <td style={S.td}>{r.status}</td>
                  <td style={S.td}>
                    {r.status === 'new' && ['rfq', 'negotiation', 'order_intent'].includes(r.intent) && (
                      <button style={S.btnPrimary} disabled={busy} onClick={() => negotiate(r.id)}>{t('开启谈判')}</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无询盘')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 谈判 ═══
function NegotiationsView() {
  const [rows, setRows] = useState<any[]>([]);
  const [detail, setDetail] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const load = () => get('/api/v1/negotiations').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = async (id: string) => {
    try { const r: any = await get(`/api/v1/negotiations/${id}`); setDetail(r.data); } catch (e: any) { alert(e.message); }
  };

  const reply = async (id: string) => {
    const msg = prompt('粘贴买家最新回复：');
    if (!msg) return;
    setBusy(true);
    try {
      const r: any = await post(`/api/v1/negotiations/${id}/respond`, { buyer_message: msg });
      if (r.data.guardrail_corrections?.length) alert(`⚠️ 毛利护栏触发：\n${r.data.guardrail_corrections.join('\n')}`);
      await open(id); load();
    } catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  const contract = async (id: string) => {
    setBusy(true);
    try { const r: any = await post(`/api/v1/negotiations/${id}/contract`); alert('合同草稿已生成，请到"合同"页查看'); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <>
      {detail && (
        <div style={{ ...S.card, background: '#f8f9fa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <strong>谈判 · {STATE_NAMES[detail.state] || detail.state} · 第 {detail.round} 轮</strong>
            <span style={{ display: 'inline-flex', gap: 6 }}>
              {!String(detail.state).startsWith('closed') && <button style={S.btnPrimary} disabled={busy} onClick={() => reply(detail.id)}>{t('输入买家回复')}</button>}
              {detail.state === 'closed_won' && <button style={S.btnPrimary} onClick={() => contract(detail.id)}>{t('📄 生成合同')}</button>}
              <button style={S.btn} onClick={() => setDetail(null)}>{t('关闭')}</button>
            </span>
          </div>
          {JSON.parse(detail.history_json || '[]').map((h: any, i: number) => (
            <div key={i} style={{ marginBottom: 8, fontSize: 13, textAlign: h.actor === 'buyer' ? 'right' : 'left' }}>
              <div style={{
                display: 'inline-block', padding: '8px 12px', borderRadius: 12, maxWidth: '80%', textAlign: 'left',
                background: h.actor === 'buyer' ? '#e6f4ea' : '#e8f0fe',
              }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>R{h.round} · {h.actor === 'buyer' ? '买家' : t('AI 谈判官')}</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{(h.message || '').slice(0, 600)}</div>
                {h.offer_summary?.total > 0 && <div style={{ fontWeight: 700, marginTop: 4 }}>报价总额：${h.offer_summary.total}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('买家')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('轮次')}</th><th style={S.th}>{t('当前报价')}</th><th style={S.th}>{t('更新时间')}</th></tr></thead>
          <tbody>
            {rows.map((r: any) => {
              const offer = JSON.parse(r.current_offer_json || '{}');
              return (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => open(r.id)}>
                  <td style={{ ...S.td, color: '#1a73e8' }}>{r.buyer_company || r.buyer_email}</td>
                  <td style={S.td}>{STATE_NAMES[r.state] || r.state}</td>
                  <td style={S.td}>{r.round}/{r.max_rounds}</td>
                  <td style={{ ...S.td, fontWeight: 700 }}>{offer.total ? `$${offer.total}` : '-'}</td>
                  <td style={S.td}>{(r.updated_at || '').slice(0, 16)}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无谈判 — 从询盘页开启')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 定价规则 ═══
function RulesView() {
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState({ product_ref: '', unit_cost: 0, retail_price: 0, floor_margin_pct: 15, moq: 50 });

  const load = () => get('/api/v1/rules').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.product_ref) return alert('请填写产品关键词');
    try { await post('/api/v1/rules', form); setForm({ ...form, product_ref: '' }); load(); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <div style={S.card}>
      <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('批发定价规则（谈判底价护栏）')}</h3>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input style={{ ...S.input, width: 180 }} placeholder={t('产品名/SKU 关键词')} value={form.product_ref} onChange={e => setForm({ ...form, product_ref: e.target.value })} />
        <input style={{ ...S.input, width: 100 }} type="number" placeholder={t('成本$')} title={t('单位成本')} value={form.unit_cost || ''} onChange={e => setForm({ ...form, unit_cost: Number(e.target.value) })} />
        <input style={{ ...S.input, width: 100 }} type="number" placeholder={t('零售价$')} title={t('零售价')} value={form.retail_price || ''} onChange={e => setForm({ ...form, retail_price: Number(e.target.value) })} />
        <input style={{ ...S.input, width: 110 }} type="number" placeholder={t('底线毛利%')} title={t('毛利底线')} value={form.floor_margin_pct} onChange={e => setForm({ ...form, floor_margin_pct: Number(e.target.value) })} />
        <input style={{ ...S.input, width: 90 }} type="number" placeholder="MOQ" title={t('最低起订量')} value={form.moq} onChange={e => setForm({ ...form, moq: Number(e.target.value) })} />
        <button style={S.btnPrimary} onClick={add}>{t('添加')}</button>
      </div>
      <table style={S.table}>
        <thead><tr><th style={S.th}>{t('产品')}</th><th style={S.th}>{t('成本')}</th><th style={S.th}>{t('零售价')}</th><th style={S.th}>{t('底线毛利')}</th><th style={S.th}>MOQ</th><th style={S.th}>{t('阶梯')}</th><th style={S.th}></th></tr></thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id}>
              <td style={S.td}>{r.product_ref}</td>
              <td style={S.td}>${r.unit_cost}</td>
              <td style={S.td}>${r.retail_price}</td>
              <td style={S.td}>{r.floor_margin_pct}%</td>
              <td style={S.td}>{r.moq}</td>
              <td style={{ ...S.td, fontSize: 12 }}>{JSON.parse(r.tiers_json || '[]').map((t: any) => `${t.min_qty}+→${t.discount_pct}%off`).join('，')}</td>
              <td style={S.td}><button style={S.btn} onClick={async () => { await del(`/api/v1/rules/${r.id}`); load(); }}>{t('删除')}</button></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={S.td} colSpan={7}>{t('暂无规则 — 谈判前请先配置产品成本与底线')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ═══ 合同 ═══
function ContractsView() {
  const [rows, setRows] = useState<any[]>([]);
  const [detail, setDetail] = useState<any>(null);

  const load = () => get('/api/v1/contracts').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = async (id: string) => {
    try { const r: any = await get(`/api/v1/contracts/${id}`); setDetail(r.data); } catch (e: any) { alert(e.message); }
  };

  const draftOrder = async (id: string) => {
    try { const r: any = await post(`/api/v1/contracts/${id}/draft-order`); alert(`Draft Order 已创建：${r.data.draft_order.name}\n${r.data.draft_order.invoiceUrl || ''}`); load(); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <>
      {detail && (
        <div style={{ ...S.card, background: '#f8f9fa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <strong>合同草稿（${detail.total_amount}）</strong>
            <span style={{ display: 'inline-flex', gap: 6 }}>
              {!detail.draft_order_id && <button style={S.btnPrimary} onClick={() => draftOrder(detail.id)}>{t('🛒 创建批发订单')}</button>}
              <button style={S.btn} onClick={() => setDetail(null)}>{t('关闭')}</button>
            </span>
          </div>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, maxHeight: 400, overflow: 'auto', background: '#fff', padding: 12, borderRadius: 8 }}>{detail.draft_text}</pre>
        </div>
      )}
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('合同')}</th><th style={S.th}>{t('金额')}</th><th style={S.th}>Draft Order</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('时间')}</th></tr></thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => open(r.id)}>
                <td style={{ ...S.td, color: '#1a73e8' }}>{r.id.slice(0, 8)}...</td>
                <td style={{ ...S.td, fontWeight: 700 }}>${r.total_amount}</td>
                <td style={S.td}>{r.draft_order_id ? '✅' : '-'}</td>
                <td style={S.td}>{r.status}</td>
                <td style={S.td}>{(r.created_at || '').slice(0, 16)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无合同 — 谈判成交后生成')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function App() {
  const [tab, setTab] = useState('inquiries');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>B2BNegotiateBot</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('B2B 批发智能谈判 — 询盘解析 · 毛利护栏状态机 · 阶梯合同 · 批发订单')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'inquiries')} onClick={() => setTab('inquiries')}>{t('询盘')}</div>
        <div style={S.tab(tab === 'negotiations')} onClick={() => setTab('negotiations')}>{t('谈判')}</div>
        <div style={S.tab(tab === 'rules')} onClick={() => setTab('rules')}>{t('定价规则')}</div>
        <div style={S.tab(tab === 'contracts')} onClick={() => setTab('contracts')}>{t('合同')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'inquiries' && <InquiriesView />}
      {tab === 'negotiations' && <NegotiationsView />}
      {tab === 'rules' && <RulesView />}
      {tab === 'contracts' && <ContractsView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
